/**
 * The turn controls on a unit inspector's header — shared by the mech, NPC and pilot bodies
 * (`turn-state-on-the-map` 3.3, `decisions/0190` point 2). They take over what the TURN rail's chip
 * buttons did:
 *
 *   - **A state line**: READY / ACTIVE / ON DECK / DONE from `perUnitState`, plus `TURN n/m` for an
 *     ELITE/ULTRA unit. The canvas carries the diamond; this is the unit's accessible statement of
 *     its state.
 *   - **END TURN** (READY or ACTIVE). On a READY unit it passes: takes and immediately ends the
 *     unit's turn, composed client-side as `activate_unit → end_turn` so each step is still
 *     hard-checked by the reducer, and is offered to the unit's controllers and to the GM on every
 *     READY unit. On the ACTIVE unit it is the bodies' own `end_turn`. Both confirm first while the
 *     unit has actions left (`useConfirmEndTurn`, `turn-presence` Phase 3); it was PASS on a READY
 *     unit until that phase made it one button with the menu's.
 *   - **START TURN** (READY, GM only, player-owned units only): the rail's GO, for the GM opening
 *     a player's unit's turn for its player without acting through it. Acting through any ready
 *     unit starts its turn too (`controlsActivation`), so a GM-owned unit has no button.
 *
 * The server stays the wall; this mirrors its gates.
 */
import { ACTIONS_PER_TURN, controlsActivation, controlsCombatant, isCombatOver, maxActivations, perUnitState, type Combatant, type CombatantId, type EncounterState, type UnitActivationState } from "@lancer-vtt/domain";
import type { UseEncounter } from "../useEncounter";
import { actionsLeftToEnd, useConfirmEndTurn } from "../useConfirmEndTurn";

const STATE_TEXT: Record<UnitActivationState, string> = {
  ready: "READY",
  active: "ACTIVE",
  on_deck: "ON DECK",
  activated: "DONE",
};

export function TurnControls({
  combatantId,
  state,
  actorId,
  encounter,
  canControl,
  isActiveActor,
  onEndTurn,
  noun,
}: {
  combatantId: CombatantId;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
  /** The server's ownership gate, mirrored (GM, or this unit's controller). Gates END TURN. */
  canControl: boolean;
  /** This unit is mid-turn — END TURN's own legality. */
  isActiveActor: boolean;
  onEndTurn: () => void;
  /** "mech" / "NPC" / "pilot", for the button titles. */
  noun: string;
}) {
  const c = state.combatants[combatantId];
  // A finished fight (`end_combat`) has no turns to start, pass or end.
  if (!c || isCombatOver(state)) return null;
  const turnState = perUnitState(state)[combatantId];
  if (!turnState) return null;
  const isGm = encounter.isGm;
  const ready = turnState === "ready";
  // Players pass their own units; the GM passes any READY unit (an absent player's mech included).
  const canPass = !!actorId && ready && (isGm || controlsActivation(c, actorId, false));
  // START TURN: the GM opens a player-owned unit's turn for its player without acting through it.
  const canStart = !!actorId && ready && isGm && (c.controllerSlotId ?? null) !== null && !controlsCombatant(c, actorId);

  const pass = () => {
    encounter.dispatch({ type: "activate_unit", actorId, combatantId });
    encounter.dispatch({ type: "end_turn", actorId, combatantId });
  };
  const start = () => encounter.dispatch({ type: "activate_unit", actorId, combatantId });

  return (
    <>
      <TurnStateText combatantId={combatantId} state={state} />
      {canStart && (
        <button type="button" className="unit-sheet-turnbtn is-primary" title={`Start this ${noun}'s turn for its player (activate_unit)`} onClick={start}>
          START TURN
        </button>
      )}
      {canPass && <EndTurnButton className="unit-sheet-turnbtn" combatant={c} state={state} onEnd={pass} title={`Pass: take and immediately end this ${noun}'s turn (activate_unit → end_turn)`} />}
      {isActiveActor && (
        <EndTurnButton className="unit-sheet-activate is-active" combatant={c} state={state} onEnd={onEndTurn} disabled={!actorId || !canControl} title={`End this ${noun}'s turn (end_turn)`} />
      )}
    </>
  );
}

/**
 * The inspector's END TURN, confirming first while the unit has actions left. A READY unit has its
 * whole turn left. Passing a READY unit wears the quiet secondary style; ending an open turn, the
 * solid one.
 */
function EndTurnButton(props: { className: string; combatant: Combatant; state: EncounterState; onEnd: () => void; title: string; disabled?: boolean }) {
  const { className, combatant, state, onEnd, title, disabled = false } = props;
  const turn = state.activation.turn;
  const ap = turn.phase === "acting" && turn.actor === combatant.id ? turn.budget.actions : ACTIONS_PER_TURN;
  const confirm = useConfirmEndTurn({ actionsLeft: actionsLeftToEnd({ ap, combatant }), ap, onEnd });
  return (
    <button
      type="button"
      className={`${className}${confirm.armed ? " is-confirm" : ""}`}
      disabled={disabled}
      title={title}
      onClick={confirm.onClick}
      onPointerLeave={confirm.onPointerLeave}
    >
      {confirm.label}
    </button>
  );
}

/**
 * The state line's text alone: READY / ACTIVE / ON DECK / DONE, plus `TURN n/m` for an ELITE/ULTRA
 * unit. {@link TurnControls} leads with it; the unit sheet shows it bare where the viewer has no
 * turn buttons (a hostile NPC) or gets the granted-turn fold's own button instead.
 */
export function TurnStateText({ combatantId, state }: { combatantId: CombatantId; state: EncounterState }) {
  const c = state.combatants[combatantId];
  const turnState = perUnitState(state)[combatantId];
  if (!c || !turnState) return null;
  const max = maxActivations(state, c);
  const used = state.activation.activationsUsed[combatantId] ?? 0;
  return (
    <span className={`unit-sheet-turnstate is-${turnState.replace("_", "-")}`} data-testid="inspector-turn-state">
      {STATE_TEXT[turnState]}
      {max > 1 && turnState !== "activated" && ` · TURN ${Math.min(used + 1, max)}/${max}`}
    </span>
  );
}
